/**
 * Mapping review queue — approve / reject pending case_subtype_mappings.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  LinkIcon,
  ArrowPathIcon,
  StarIcon as StarOutlineIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import {
  listMappings,
  approveMapping,
  rejectMapping,
  setMappingDefault,
  type CaseSubtypeMapping,
  type CaseSubtypeLifecycle,
  LIFECYCLE_COLORS,
  LIFECYCLE_LABELS,
} from './caseSubtypeTaxonomyApi';

interface Props {
  stateCode: string;
  practiceArea: string;
  /** Coverage-tab seed: override the default 'pending' filter on first mount. */
  initialStatus?: CaseSubtypeLifecycle | 'all';
  /** Coverage-tab drilldown seed: filter to a specific canonical_key. */
  initialCanonicalKey?: string;
  /** Coverage-tab drilldown seed: scroll this row into view and flash it. */
  highlightId?: string;
  onChanged: () => void;
}

const STATUS_OPTIONS: Array<{ value: CaseSubtypeLifecycle | 'all'; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'validated', label: 'Validated' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'stale', label: 'Stale' },
  { value: 'all', label: 'All' },
];

const MappingReviewQueue: React.FC<Props> = ({
  stateCode,
  practiceArea,
  initialStatus,
  initialCanonicalKey,
  highlightId,
  onChanged,
}) => {
  const [status, setStatus] = useState<CaseSubtypeLifecycle | 'all'>(initialStatus ?? 'pending');
  const [canonicalFilter] = useState<string | undefined>(initialCanonicalKey);
  const [rows, setRows] = useState<CaseSubtypeMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionNotes, setActionNotes] = useState<Record<string, string>>({});

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { mappings } = await listMappings({
        status,
        state_code: stateCode || undefined,
        practice_area: practiceArea || undefined,
        limit: 200,
      });
      const filtered = canonicalFilter
        ? mappings.filter((m) => m.canonical_key === canonicalFilter)
        : mappings;
      setRows(filtered);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load mappings');
    } finally {
      setLoading(false);
    }
  }, [status, stateCode, practiceArea, canonicalFilter]);

  useEffect(() => { reload(); }, [reload]);

  // Scroll the highlight target into view once rows are present.
  useEffect(() => {
    if (!highlightId || loading) return;
    const el = document.getElementById(`mapping-row-${highlightId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightId, loading, rows]);

  const handleApprove = async (row: CaseSubtypeMapping) => {
    setBusyId(row.id);
    try {
      await approveMapping(row.id, { review_notes: actionNotes[row.id] || undefined });
      onChanged();
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Approve failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (row: CaseSubtypeMapping) => {
    const notes = actionNotes[row.id] || '';
    if (!notes.trim()) {
      setError('Reject requires review notes.');
      return;
    }
    setBusyId(row.id);
    try {
      await rejectMapping(row.id, { review_notes: notes });
      onChanged();
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reject failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleSetDefault = async (row: CaseSubtypeMapping) => {
    setBusyId(row.id);
    try {
      await setMappingDefault(row.id);
      onChanged();
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Set-default failed');
    } finally {
      setBusyId(null);
    }
  };

  // Find validated sibling groups — canonicals with more than one validated
  // row in the same scope. Only these rows show the "Set as default" action.
  const siblingKey = (r: CaseSubtypeMapping) =>
    `${r.practice_area}::${r.scope_type}::${r.scope_type === 'court' ? r.jurisdiction_code : r.state_code}::${r.canonical_key}`;
  const validatedSiblingCounts = new Map<string, number>();
  for (const r of rows) {
    if (r.lifecycle_status !== 'validated') continue;
    const k = siblingKey(r);
    validatedSiblingCounts.set(k, (validatedSiblingCounts.get(k) || 0) + 1);
  }

  return (
    <div className="space-y-3" style={{ animation: 'fadeSlideIn 300ms ease-out forwards' }}>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1 bg-gray-100/80 dark:bg-gray-900/50 rounded-lg p-1">
          {STATUS_OPTIONS.map((o) => {
            const active = o.value === status;
            return (
              <button
                key={o.value}
                onClick={() => setStatus(o.value)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  active
                    ? 'bg-white dark:bg-gray-700 shadow-sm text-violet-700 dark:text-violet-300 ring-1 ring-violet-500/20'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
        {canonicalFilter && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
            canonical: {canonicalFilter}
          </span>
        )}
        <button
          onClick={reload}
          className="ml-auto inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          title="Refresh"
        >
          <ArrowPathIcon className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 rounded-lg p-3 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-violet-500 border-t-transparent" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
          No mappings match the current filter.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-3 py-2 text-left">Scope</th>
                <th className="px-3 py-2 text-left">Practice Area</th>
                <th className="px-3 py-2 text-left">Local → Canonical</th>
                <th className="px-3 py-2 text-left">Source</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Review Notes</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {rows.map((m) => (
                <tr
                  key={m.id}
                  id={`mapping-row-${m.id}`}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-900/30 ${
                    highlightId === m.id ? 'ring-2 ring-violet-400 ring-inset bg-violet-50/40 dark:bg-violet-900/10' : ''
                  }`}
                >
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    <div className="font-medium uppercase">{m.scope_type}</div>
                    <div className="text-gray-400">
                      {m.scope_type === 'court' ? m.jurisdiction_code : m.state_code || '—'}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-200 whitespace-nowrap">
                    {m.practice_area}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {m.local_label}
                      </span>
                      {m.is_default_for_scope && (
                        <StarSolidIcon
                          className="h-3.5 w-3.5 text-amber-500"
                          aria-label="Scope default"
                          title="Default display term for this canonical in this scope"
                        />
                      )}
                    </div>
                    <div className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                      {m.local_key} → {m.canonical_key}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                    {m.source_url ? (
                      <a
                        href={m.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-violet-600 dark:text-violet-400 hover:underline"
                      >
                        <LinkIcon className="h-3 w-3" />
                        {m.source_authority || 'source'}
                      </a>
                    ) : (
                      <span className="text-gray-400">{m.source_authority || '—'}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${LIFECYCLE_COLORS[m.lifecycle_status]}`}
                    >
                      {LIFECYCLE_LABELS[m.lifecycle_status]}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {m.lifecycle_status === 'pending' ? (
                      <input
                        type="text"
                        placeholder="Reviewer note…"
                        value={actionNotes[m.id] || ''}
                        onChange={(e) =>
                          setActionNotes((prev) => ({ ...prev, [m.id]: e.target.value }))
                        }
                        className="w-48 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-xs text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-violet-500"
                      />
                    ) : (
                      <span className="text-xs text-gray-400 dark:text-gray-500 line-clamp-2">
                        {m.review_notes || '—'}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {m.lifecycle_status === 'pending' ? (
                      <div className="inline-flex items-center gap-1.5">
                        <button
                          disabled={busyId === m.id}
                          onClick={() => handleApprove(m)}
                          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white px-2 py-1 text-xs font-medium disabled:opacity-50"
                        >
                          <CheckCircleIcon className="h-3.5 w-3.5" />
                          Approve
                        </button>
                        <button
                          disabled={busyId === m.id}
                          onClick={() => handleReject(m)}
                          className="inline-flex items-center gap-1 rounded-md bg-rose-600 hover:bg-rose-500 text-white px-2 py-1 text-xs font-medium disabled:opacity-50"
                        >
                          <XCircleIcon className="h-3.5 w-3.5" />
                          Reject
                        </button>
                      </div>
                    ) : m.lifecycle_status === 'validated'
                        && (validatedSiblingCounts.get(siblingKey(m)) || 0) > 1
                        && !m.is_default_for_scope ? (
                      <button
                        disabled={busyId === m.id}
                        onClick={() => handleSetDefault(m)}
                        className="inline-flex items-center gap-1 rounded-md bg-amber-500 hover:bg-amber-400 text-white px-2 py-1 text-xs font-medium disabled:opacity-50"
                        title="Make this the default display term for this canonical in this jurisdiction"
                      >
                        <StarOutlineIcon className="h-3.5 w-3.5" />
                        Set as default
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MappingReviewQueue;
